import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import LogoDark from '@images/logoDark.svg';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { useNavigate } from 'react-router-dom';
import FormFieldComponent from '@/components/FormFieldComponent';
import { useUserForgotPasswordPost } from '@brainvaulttech/dgr-react-sdk';
import { toast } from '@/components/ui/use-toast';
import { displayError } from '@/lib/error';
import { EMAIL_ADDRESS_VALIDATION } from '@/utils/constant';

const ForgotFormSchema = z.object({
  email: z.string({ message: 'Email address is required' }).email({ message: 'Email address is invalid' }),
});

export const ForgotPassword = () => {
  const navigate = useNavigate();
  const form = useForm<z.infer<typeof ForgotFormSchema>>({
    resolver: zodResolver(ForgotFormSchema),
    mode: 'onBlur',
  });
  const {
    formState: { isValid },
  } = form;
  const { mutate: forgotPassword, status } = useUserForgotPasswordPost();

  const onSubmitHandler = (values: z.infer<typeof ForgotFormSchema>) => {
    forgotPassword(
      { email: values.email },
      {
        onSuccess: (data) => {
          navigate('/login');
          toast({
            description: data.data.message,
            className: '!relative !top-[-60px]',
            variant: 'default',
            duration: 5000,
          });
        },
        onError: (e) => {
          displayError(e, 5000);
        },
      }
    );
  };

  return (
    <div className="bg-white flex flex-col items-center justify-center h-screen min-h-[600px] ">
      <Form {...form}>
        <Card className="flex bg-white rounded- shadow w-[30%] min-w-[325px] max-w-[700px] max-h-[535px] px-8 pt-8 pb-4">
          <CardContent className="flex flex-col gap-10 w-full p-0">
            <div className="w-full flex justify-center">
              <img src={LogoDark} alt="logo" className="w-[135px] h-[70px]" />
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-3xl font-semibold" data-testId="forgot-password">
                Forgot Password
              </span>
              <span className="text-[16px] font-normal text-placeholder">
                Enter the email address associated with your account.
              </span>
            </div>
            <form onSubmit={form.handleSubmit(onSubmitHandler)}>
              <FormFieldComponent
                control={form.control}
                name="email"
                label="Email Address"
                placeholder="Enter Email Address"
                required
                {...EMAIL_ADDRESS_VALIDATION}
              />
              <div className="flex flex-col justify-start gap-10">
                <Button
                  type="submit"
                  className="w-full text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 text-sm font-medium mt-6"
                  disabled={status === 'pending' || !isValid}
                  isLoading={status === 'pending'}
                >
                  Submit
                </Button>
                <div className="flex text-sm justify-center">
                  <span className="text-[#3B82F6] cursor-pointer" onClick={() => navigate('/login')}>
                    Back to Log In
                  </span>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </Form>
    </div>
  );
};
